import { memo, useMemo, useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LABEL, CUSTOM_MESSAGES } from '@veriables';
import { deleteItm, addChecks } from '@reducers/orders';
import { updateChecksAfterResponse } from '@reducers/storeChecks';
import { setErrorPopUp } from '@reducers/notification';
import { safeJsonParse } from '@utility/helperFunction';
import getDeviceType from '@utility/deviceType';
import { useFooterActions } from './cartHandler';
import { useConfirmFooterModal } from './useConfirmFooterModal';
import { useCartTab } from './useCartTab';
import { useActiveCards } from './useActiveCards';
import { useVoidFlow } from './useVoidFlow';
import { useRowActions } from './useRowActions';
import { ROW_ACTIONS_BY_TAB, BULK_ACTIONS } from './cartConstants';
import CartRow from './cartRow';
import CartTabs from './CartTabs';
import CartHeader from './CartHeader';
import CartFooter from './CartFooter';
import HoldItemsModal from '@components/HoldController/HoldItemsModal/holdItemsModal';
import CustomEventEmitter from '@utility/customEventEmitter';


const ConfirmationPopUpV0 = lazy(() => import('@components/checksCommon/confirmationPopUpCopy'));
const ManagerCheck = lazy(() => import('@components/checksCommon/managerCheck'));
const VoidCheckModal = lazy(() => import('@components/storeChecks/Modals/voidCheck'));
const SendOrderModeReVamp = lazy(() => import('@components/checksCommon/SendOrderModeReVamp'));

const Cart = ({ seat, serverAppTheme }) => {
  const position = useSelector(s => s.orders.position);
  const isOnlyOrderMode = useSelector(
    s => s.oauth.deviceInformation?.deviceGroup?.serverAppSettings?.disableOrdering
  );
  const menuItemsHashTable = useSelector(s => s.orders.menuItemsHashTable);
  const { activeTab, setActiveTab, items, pendingItems, sentItems } = useCartTab(seat, {
    position,
    isOnlyOrderMode,
    menuItemsHashTable,
  });
  const checkSeats = seat?.seats ?? [];
  const allMenusHashTable = useSelector(s => s.orders.allMenusHashTable);

  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [orderModeData, setOrderModeData] = useState({ isOpen: false, data: null, type: null });
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setCheckedIds(new Set());
    setOrderModeData(prev => ({ ...prev, isOpen: false }));
    setExpandedId(null);
  }, [seat?.id, activeTab]);

  useEffect(() => {
    const handleClose = () => {
      setOrderModeData(prev => (prev.isOpen ? { ...prev, isOpen: false } : prev));
    };
    CustomEventEmitter.addCustomListerner('closeOrderSendMode', handleClose);
    return () => {
      CustomEventEmitter.removeCustomListener('closeOrderSendMode', handleClose);
    };
  }, []);


  useEffect(() => {
    setCheckedIds(prev => {
      if (prev.size === 0) return prev;
      const validIds = new Set(items.map(i => i.id));
      const pruned = new Set([...prev].filter(id => validIds.has(id)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [items]);

  const toggle = useCallback(id => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setExpandedId(null);
    setOrderModeData(prev => (prev.isOpen ? { ...prev, isOpen: false } : prev));
  }, []);

  const allChecked = items.length > 0 && items.every(i => checkedIds.has(i.id));
  const hasChecked = checkedIds.size > 0;

  const toggleAll = useCallback(
    checked => {
      setCheckedIds(checked ? new Set(items.map(i => i.id)) : new Set());
      setExpandedId(null);
      setOrderModeData(prev => (prev.isOpen ? { ...prev, isOpen: false } : prev));
    },
    [items]
  );

  const onExpandRow = useCallback(id => {
    setExpandedId(prev => (prev === id ? null : id));
    setCheckedIds(new Set());
  }, []);

  const dispatch = useDispatch();
  const deviceInfo = useSelector(s => s.oauth.deviceInformation);
  const userInfo = useSelector(s => s.oauth.loggedInUserData);
  const menusList = useSelector(s => s.orders.menusList);
  const auth = useSelector(s => s.oauth.authInfo);
  const { deleteAll, sendAll: rawSendAll, sentMove, unsentMove, repeat, refill } = useFooterActions(seat);

  const enableItemHold = deviceInfo?.deviceGroup?.serverAppSettings?.enableItemHold;

  const orderSendModes = deviceInfo?.deviceGroup?.orderSendModes;
  const shouldEnableOrder = !(
    orderSendModes?.doNoMake === false &&
    orderSendModes?.toGo === false &&
    orderSendModes?.rush === false
  );
  const showOrderSendMode = !!(deviceInfo?.deviceGroup && shouldEnableOrder);

  const allowVoid = useMemo(() => {
    const job = auth?.jobs?.find(j => j.id === userInfo?.jobId);
    return !!(auth?.nonIntegratedPos ? job?.allowVoid : false);
  }, [auth, userInfo?.jobId]);

  const rowActions = useMemo(() => {
    const { isCounter: deviceIsCounter, isDriveThru, isCallCenter } = getDeviceType();
    const isCounterMode = deviceIsCounter || isDriveThru || isCallCenter;

    let base = ROW_ACTIONS_BY_TAB[activeTab];

    if (isCounterMode) {
      if (activeTab === 'sent') return [];
      return base.filter(a => a.key !== 'move' && a.key !== 'send');
    }

    if (activeTab === 'sent' && seat?.sourceType !== 0) {
      base = base.filter(a => a.key !== 'refill');
    }
    if (activeTab === 'sent' && allowVoid && seat?.open !== false) {
      return [...base, { key: 'void', name: LABEL().VOID, bg: 'rgb(116, 164, 255)' }];
    }
    return base;
  }, [activeTab, allowVoid, seat?.open, seat?.sourceType]);

  const isCounter = useMemo(
    () => !!(deviceInfo?.deviceGroup && [1].includes(deviceInfo.deviceGroup.startMode)),
    [deviceInfo]
  );

  const activeCardsByCheckItemId = useActiveCards(seat, sentItems, pendingItems);

  const sentSelectedActions = useMemo(() => {
    const actions = [
      { key: 'sentMove', name: LABEL().MOVE },
      { key: 'sentRepeat', name: LABEL().REPEAT },
    ];
    if (allowVoid) actions.push({ key: 'sentVoid', name: LABEL().VOID });
    return actions;
  }, [allowVoid]);

  const [confirmPopup, setConfirmPopup] = useState({ open: false, type: null });
  const [holdModalState, setHoldModalState] = useState({ isOpen: false, items: [] });
  const {
    isVoidOpen, setIsVoidOpen, isManagerCheck,
    handleManagerAuthentication, handleVoidItems,
  } = useVoidFlow(seat);

  const getPackLinkedItems = useCallback(
    item => {
      const uid = item?.uniqueIdForGameCardItem ||
        safeJsonParse(item?.modifierJson, {}).uniqueIdForGameCardItem || null;
      if (!uid) return [item];
      const allPending = seat?.orders?.[0]?.items ?? [];
      const linked = allPending
        .filter(oi => {
          if (!oi.pending) return false;
          const oJson = safeJsonParse(oi.orderJson, {});
          const mJson = safeJsonParse(oJson.modifierJson, {});
          return mJson.uniqueIdForGameCardItem === uid;
        })
        .map(oi => {
          const oJson = safeJsonParse(oi.orderJson, {});
          const mJson = safeJsonParse(oJson.modifierJson, {});
          return { ...oi, ...oJson, ...mJson, id: oi.id, checkItemId: oi.checkItemId, modifierJson: oJson.modifierJson };
        });
      return linked.length > 0 ? linked : [item];
    },
    [seat?.orders]
  );

  const runDelete = useCallback(
    target => {
      if (!target?.length || !seat?.id) return;
      const payload = {
        storeId: deviceInfo?.storeId,
        deviceId: deviceInfo?.deviceId,
        employeeId: userInfo?.id,
        checkHeaderId: seat.id,
        employeeCard: userInfo?.employeeCard,
        items: target.map(i => ({ checkOrderItemId: i.id })),
      };
      dispatch(deleteItm(payload))
        .then(res => dispatch(updateChecksAfterResponse(res)))
        .catch(err => dispatch(setErrorPopUp({ res: err.message })));
    },
    [dispatch, deviceInfo, userInfo, seat?.id]
  );


  const handleApplyHold = useCallback(
    itemsWithHoldTime => {
      const dataToSend = {
        storeId: deviceInfo?.storeId,
        deviceId: deviceInfo?.deviceId,
        employeeId: Number(userInfo?.id),
        employeeCard: userInfo?.employeeCard,
        checkHeaderId: seat?.id,
        stationId: seat?.stationId,
        items: itemsWithHoldTime.map(item => ({ ...item, checkOrderItemId: item.id })),
        orderJson: JSON.stringify(itemsWithHoldTime),
        longitude: '0',
        latitude: '0',
        geoLocation: '0',
      };
      dispatch(addChecks(dataToSend, 'update'))
        .then(res => {
          dispatch(updateChecksAfterResponse(res));
          setHoldModalState({ isOpen: false, items: [] });
        })
        .catch(err => dispatch(setErrorPopUp({ res: err.message })));
    },
    [dispatch, deviceInfo, userInfo, seat]
  );

  const onConfirmClose = useCallback(
    (type, selectedSeats) => {
      if (type === 'yes') {
        switch (confirmPopup.type) {
          case 'sentMove': {
            sentMove(confirmPopup.data.selectedItem, selectedSeats)?.then(success => success && pendingItems.length > 0 && setActiveTab('pending'));
            break;
          }
          case 'unsentMove':
            unsentMove(confirmPopup.data.selectedItem, selectedSeats);
            break;
          default:
            break;
        }
      }
      setConfirmPopup({ open: false, type: null });
    },
    [confirmPopup, sentMove, unsentMove, setActiveTab]
  );

  const { request: requestConfirm, jsx: confirmJsx } = useConfirmFooterModal({
    onDeleteAll: deleteAll,
    onDeleteSelected: () => {
      const checkedItems = items.filter(i => checkedIds.has(i.id));
      const packUids = new Set();
      const regularItems = [];
      checkedItems.forEach(item => {
        const isPackItem = menuItemsHashTable?.[item?.menuItemId]?.minOrderQuantity > 0 ||
          menuItemsHashTable?.[item?.menuItemId]?.cardType === 1;
        if (isPackItem) {
          const uid = item?.uniqueIdForGameCardItem ||
            safeJsonParse(item?.modifierJson, {}).uniqueIdForGameCardItem;
          if (uid) packUids.add(uid);
        } else {
          regularItems.push(item);
        }
      });
      if (!packUids.size) return runDelete(regularItems);
      const allPending = seat?.orders?.[0]?.items ?? [];
      const packItems = allPending
        .filter(oi => {
          if (!oi.pending) return false;
          const mJson = safeJsonParse(safeJsonParse(oi.orderJson, {}).modifierJson, {});
          return packUids.has(mJson.uniqueIdForGameCardItem);
        })
        .map(oi => {
          const oJson = safeJsonParse(oi.orderJson, {});
          const mJson = safeJsonParse(oJson.modifierJson, {});
          return { ...oi, ...oJson, ...mJson, id: oi.id, checkItemId: oi.checkItemId, modifierJson: oJson.modifierJson };
        });
      const packIds = new Set(packItems.map(i => i.id));
      runDelete([...packItems, ...regularItems.filter(i => !packIds.has(i.id))]);
    },
    onDeleteRow: item => {
      const isPackItem = menuItemsHashTable?.[item?.menuItemId]?.minOrderQuantity > 0 ||
        menuItemsHashTable?.[item?.menuItemId]?.cardType === 1;
      runDelete(isPackItem ? getPackLinkedItems(item) : [item]);
    },
    onSendAll: () => {
      const unheldItems = pendingItems.filter(item => !item.sendDate);
      if (unheldItems.length < pendingItems.length && unheldItems.length > 0) {
        rawSendAll(null, null, unheldItems);
      } else {
        rawSendAll(null);
      }
    },
    onSendSelected: () => {
      const target = items.filter(i => checkedIds.has(i.id));
      if (target.length) rawSendAll(null, null, target);
    },
    onSendRow: item => rawSendAll(null, null, [item]),
    serverAppTheme,
  });

  const onItemAction = useRowActions({
    seat,
    activeTab,
    menuItemsHashTable,
    menusList,
    repeat,
    refill,
    requestConfirm,
    handleManagerAuthentication,
    setConfirmPopup,
  });

  const onBulkAction = useCallback(
    key => {
      if (key === 'deleteAll') return requestConfirm('deleteAll', items.length);
      if (key === 'sendAll') {
        const unheldItems = pendingItems.filter(i => !i.sendDate);
        return requestConfirm('sendAll', unheldItems.length);
      }
      if (key === 'deleteSelected') {
        const target = items.filter(i => checkedIds.has(i.id));
        return requestConfirm('deleteSelected', target.length);
      }
      if (key === 'sendSelected') {
        const target = items.filter(i => checkedIds.has(i.id));
        return requestConfirm('sendSelected', target.length);
      }
      if (key === 'sentVoid') {
        const target = items.filter(i => checkedIds.has(i.id));
        if (target.length) {
          handleManagerAuthentication(
            { ...seat, selectedItem: target },
            LABEL().VOID_ITEM
          );
        }
        return;
      }
      if (key === 'move') {
        const target = items.filter(i => checkedIds.has(i.id));
        if (target.length) {
          setConfirmPopup({
            open: true,
            type: 'unsentMove',
            data: { ...seat, selectedItem: target },
          });
          setCheckedIds(new Set());
        }
        return;
      }
      if (key === 'hold') {
        // Use pendingItems (original order) so the hold modal shows items
        // in the same order they appeared before hold was applied.
        const target = pendingItems.filter(i => checkedIds.has(i.id));
        if (target.length) {
          setHoldModalState({ isOpen: true, items: target });
          setCheckedIds(new Set());
        }
        return;
      }
      if (key === 'repeat') {
        const target = items.filter(i => checkedIds.has(i.id));
        if (target.length) {
          repeat(target, true);
          setCheckedIds(new Set());
        }
        return;
      }
      if (key === 'sentMove') {
        const target = items.filter(i => checkedIds.has(i.id));
        if (target.length) setConfirmPopup({ open: true, type: 'sentMove', data: { ...seat, selectedItem: target } });
        return;
      }
      if (key === 'sentRepeat') {
        const target = items.filter(i => checkedIds.has(i.id));
        if (target.length) {
          repeat(target, false);
          setCheckedIds(new Set());
        }
        return;
      }
      if (key === 'delete')
        return runDelete(items.filter(i => checkedIds.has(i.id)));
      if (key === 'orderSendMode') {
        if (!showOrderSendMode) return;
        if (orderModeData.isOpen) {
          setOrderModeData(prev => ({ ...prev, isOpen: false }));
          return;
        }
        const type = hasChecked ? 'sendSelected' : 'sendAll';
        setOrderModeData({ isOpen: true, data: null, type });
        return;
      }
    },
    [items, pendingItems, checkedIds, seat, showOrderSendMode, orderModeData.isOpen, runDelete, repeat, requestConfirm, handleManagerAuthentication, setHoldModalState]
  );

  const closeSendOrderMode = useCallback(
    (mode) => {
      if (mode) {
        if (orderModeData.type === 'sendSelected') {
          const target = items.filter(i => checkedIds.has(i.id));
          if (target.length) rawSendAll(mode, null, target);
        } else {
          const unheldItems = pendingItems.filter(item => !item.sendDate);
          if (unheldItems.length < pendingItems.length && unheldItems.length > 0) {
            rawSendAll(mode, null, unheldItems);
          } else {
            rawSendAll(mode);
          }
        }
      }
      setOrderModeData(prev => ({ ...prev, isOpen: false }));
    },
    [rawSendAll, orderModeData.type, items, checkedIds, pendingItems]
  );

  const hasCheckedPackItem = useMemo(() => {
    if (checkedIds.size === 0) return false;
    return items
      .filter(i => checkedIds.has(i.id))
      .some(i => {
        const menuItem = menuItemsHashTable?.[i?.menuItemId];
        return menuItem?.minOrderQuantity > 0 || menuItem?.cardType === 1 || menuItem?.cardType === 2;
      });
  }, [checkedIds, items, menuItemsHashTable]);

  const hasCheckedDiscount = useMemo(() => {
    if (checkedIds.size === 0) return false;
    return items
      .filter(i => checkedIds.has(i.id))
      .some(
        i =>
          i?.isDiscount ||
          i?.discountId ||
          (i?.discountName && !i?.menuItemId) ||
          i?.isServiceCharge ||
          i?.serviceChargeId ||
          (i?.serviceChargeName && !i?.menuItemId)
      );
  }, [checkedIds, items]);

  const selectedActions = hasChecked
    ? hasCheckedDiscount
      ? auth?.nonIntegratedPos
        ? [{ key: 'sentMove', name: LABEL().MOVE }, { key: 'sentVoid', name: LABEL().VOID }]
        : [{ key: 'sentMove', name: LABEL().MOVE }]
      : activeTab === 'pending'
        ? hasCheckedPackItem
          ? BULK_ACTIONS.pendingSelected.filter(a => a.key === 'deleteSelected')
          : enableItemHold
            ? BULK_ACTIONS.pendingSelected
            : BULK_ACTIONS.pendingSelected.filter(a => a.key !== 'hold')
        : hasCheckedPackItem
          ? []
          : sentSelectedActions
    : null;

  const selectedQuantity = useMemo(() => {
    if (checkedIds.size === 0) return 0;
    return items.reduce(
      (sum, item) =>
        checkedIds.has(item.id) ? sum + (Number(item.quantity) || 0) : sum,
      0
    );
  }, [items, checkedIds]);

  const tabStyles = useMemo(() => ({
    sentTabBg: {
      background: serverAppTheme?.order_item_Selected_bg,
      color: serverAppTheme?.primary_text,
      border: `2px solid ${serverAppTheme?.order_item_border}`,
    },
    inactiveTabBg: {
      color: serverAppTheme?.primary_text,
      background: 'transparent',
      border: `2px solid ${serverAppTheme?.order_item_border}`,
    },
    pendingTabBg: {
      color: serverAppTheme?.primary_text,
      background: pendingItems.length > 0
        ? serverAppTheme?.pending_items
        : serverAppTheme?.order_item_Selected_bg,
      border: `2px solid ${serverAppTheme?.order_item_border}`,
    },
  }), [serverAppTheme, pendingItems.length]);

  return (
    <div className="h-100 w-25 d-flex flex-column position-relative">
      <CartTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        pendingCount={pendingItems.length}
        sentCount={sentItems.length}
        tabStyles={tabStyles}
        allChecked={allChecked}
        toggleAll={toggleAll}
        hasItems={items.length > 0}
        serverAppTheme={serverAppTheme}
      />

      <CartHeader serverAppTheme={serverAppTheme} activeTab={activeTab} />

      <div className="flex-1 overflow-auto pl-1" style={{ flex: 1, minHeight: 0 }}>
        {items.length > 0 ? (
          items.map(item => (
            <CartRow
              key={item.id ?? item.internalId}
              item={item}
              checkSeats={checkSeats}
              actions={rowActions}
              onAction={onItemAction}
              isChecked={checkedIds.has(item.id)}
              onToggle={toggle}
              isExpanded={expandedId === item.id}
              onExpand={onExpandRow}
              serverAppTheme={serverAppTheme}
              activeTab={activeTab}
              displayPrice={!!auth?.serverAppFeatures?.displayPrice}
              cardType={menuItemsHashTable?.[item.menuItemId]?.cardType}
              menuItemsHashTable={menuItemsHashTable}
              activeCards={activeCardsByCheckItemId?.[item.id]}
            />
          ))
        ) : (
          <div
            className="h-100 d-flex align-items-center justify-content-center fs_16 fw-600"
            style={{ color: serverAppTheme?.noRecord }}
          >
            {CUSTOM_MESSAGES().NO_ITEM_FOUND}
          </div>
        )}
      </div>

      {orderModeData.isOpen && (
        <Suspense fallback={null}>
          <SendOrderModeReVamp
            deviceInfo={deviceInfo}
            isOpen={orderModeData.isOpen}
            data={orderModeData.data}
            type={orderModeData.type}
            check={seat}
            onClose={closeSendOrderMode}
            allMenusHashTable={allMenusHashTable}
            menuItemsHashTable={menuItemsHashTable}
            cart={pendingItems}
            auth={auth}
            screenType={'checklist'}
            serverAppTheme={serverAppTheme}
          />
        </Suspense>
      )}

      <CartFooter
        selectedActions={selectedActions}
        count={selectedQuantity}
        onAction={onBulkAction}
        serverAppTheme={serverAppTheme}
        showOrderSendMode={showOrderSendMode}
        pendingItems={pendingItems}
        isSendOrderMode={orderModeData.isOpen}
      />

      {confirmPopup.open && confirmPopup.data && (
        <Suspense fallback={null}>
          <ConfirmationPopUpV0
            isOpen={confirmPopup.open}
            onClose={onConfirmClose}
            pendingActionType={confirmPopup}
            isCounter={isCounter}
            serverAppTheme={serverAppTheme}
            menuItemsHashTable={menuItemsHashTable}
            seat={seat}
          />
        </Suspense>
      )}
      {isManagerCheck && (
        <Suspense fallback={null}>
          <ManagerCheck
            setIsVoidOpen={setIsVoidOpen}
            setOthersPopUp={() => {}}
            isSetupOffPremise={false}
            setIsSetupOffPremise={() => {}}
          />
        </Suspense>
      )}
      {isVoidOpen && (
        <Suspense fallback={null}>
          <VoidCheckModal
            setIsVoidOpen={setIsVoidOpen}
            isVoidOpen={isVoidOpen}
            setVoidCheck={() => {}}
            handleVoidChecks={() => {}}
            handleVoidItems={handleVoidItems}
            setIsToolsPopUp={() => {}}
            serverAppTheme={serverAppTheme}
            voidCheck={false}
            seat={seat}
            setToolsModalOpen={() => {}}
          />
        </Suspense>
      )}
      {holdModalState.isOpen && (
        <HoldItemsModal
          isOpen={holdModalState.isOpen}
          items={holdModalState.items}
          checkOrders={seat?.orders}
          seat={seat}
          serverAppTheme={serverAppTheme}
          onClose={() => setHoldModalState({ isOpen: false, items: [] })}
          onApplyHold={handleApplyHold}
        />
      )}
      {confirmJsx}
    </div>
  );
};

export default memo(Cart);
